import { useMemo } from 'react';
import { NormalizedTask } from '../../domain/normalize';
import { revisionKpis, aprobadoVsRechazado, revisionPorProyecto } from '../../domain/metrics';
import { UserDoc } from '../../domain/types';
import { nameByEmail } from '../../domain/util';
import { Card, Kpi, DonutCard, BoardIntro, ExportButton } from './shared';

export default function BoardRevisiones({ tasks, users }: { tasks: NormalizedTask[]; users: UserDoc[] }) {
  const k = useMemo(() => revisionKpis(tasks), [tasks]);
  const donut = useMemo(() => aprobadoVsRechazado(tasks), [tasks]);
  const porProy = useMemo(() => revisionPorProyecto(tasks), [tasks]);
  const rechazos = useMemo(
    () => tasks
      .filter((t) => t.revision_valoracion === 'rechazado')
      .sort((a, b) => (b.fecha_entrega || '').localeCompare(a.fecha_entrega || '')),
    [tasks]
  );
  const rechazosCsv = rechazos.map((t) => ({
    proyecto: t.proyecto, tarea: t.descripcion, solicitante: t.solicitante,
    responsable: nameByEmail(t.responsable, users), motivo: t.revision_descripcion,
  }));

  return (
    <div className="grid">
      <BoardIntro
        titulo="Revisiones de cliente"
        objetivo="Resultado de la valoración del cliente sobre las entregas finalizadas: qué se aprueba, qué se rechaza y qué queda pendiente de revisar."
        preguntas={['¿Qué % de entregas aprueba el cliente?', '¿Qué proyectos concentran los rechazos?', '¿Cuántas entregas esperan revisión?']}
      />
      <div className="cards" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(180px,1fr))' }}>
        <Kpi label="Revisadas" value={String(k.revisadas)} help="Entregas con veredicto del cliente (aprobado o rechazado)." />
        <Kpi label="Aprobadas" value={String(k.aprobadas)} tone="ok" />
        <Kpi label="Rechazadas" value={String(k.rechazadas)} tone={k.rechazadas > 0 ? 'bad' : undefined} />
        <Kpi label="% aprobación" value={`${k.pctAprobacion}%`} tone={k.pctAprobacion < 70 ? 'warn' : 'ok'} help="Aprobadas sobre el total de revisadas." />
        <Kpi label="Pendientes de revisión" value={String(k.pendientes)} tone={k.pendientes > 0 ? 'warn' : undefined} help="Tareas finalizadas esperando la valoración del cliente." />
      </div>

      <div className="cards">
        <DonutCard
          title="Aprobado vs rechazado"
          hint="Veredicto del cliente sobre las entregas ya revisadas."
          data={donut}
          colors={['#16a34a', '#dc2626']}
        />
      </div>

      <Card title="Revisiones por proyecto">
        <div style={{ overflowX: 'auto', maxHeight: 420 }}>
          <table>
            <thead>
              <tr>
                <th>Proyecto</th>
                <th className="num">Aprobadas</th>
                <th className="num">Rechazadas</th>
                <th className="num">Pendientes</th>
              </tr>
            </thead>
            <tbody>
              {porProy.length === 0 && (
                <tr><td colSpan={4} className="muted">Sin revisiones todavía.</td></tr>
              )}
              {porProy.map((r) => (
                <tr key={r.name}>
                  <td>{r.name}</td>
                  <td className="num">{r.aprobado || '—'}</td>
                  <td className="num">{r.rechazado ? <span className="badge urg-alta">{r.rechazado}</span> : '—'}</td>
                  <td className="num">{r.pendiente || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card title="Detalle de rechazos" hint="Entregas rechazadas por el cliente, con el motivo indicado en la revisión." action={<ExportButton filename="rechazos.csv" rows={rechazosCsv} />}>
        <div style={{ overflowX: 'auto', maxHeight: 420 }}>
          <table>
            <thead>
              <tr><th>Proyecto</th><th>Tarea</th><th>Responsable</th><th>Motivo del rechazo</th></tr>
            </thead>
            <tbody>
              {rechazos.length === 0 && (
                <tr><td colSpan={4} className="muted">Sin rechazos en la vista.</td></tr>
              )}
              {rechazos.map((t) => (
                <tr key={t.id}>
                  <td>{t.proyecto || '—'}</td>
                  <td>{t.descripcion || '—'}</td>
                  <td>{nameByEmail(t.responsable, users)}</td>
                  <td>{t.revision_descripcion || <em className="muted">sin motivo</em>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
